<template>
  <div
    v-if="activeScan && showScanOverlay"
    class="main-qrscanner"
  >
    <div
      class="main-qrscanner-overlay"
    >
      <div class="corner top-left" />
      <div class="corner top-right" />
      <div class="corner bottom-left" />
      <div class="corner bottom-right" />
      <div class="scanner-line" />
    </div>
    <div class="main-qrscanner-actions">
      <f7-button
        v-if="scannerStatus.canEnableLight"
        class="margin-right"
        :icon-material="scannerStatus.lightEnabled ? 'flash_off' : 'flash_on'"
        @click="changeFlashStatus"
      />
      <f7-button
        v-if="scannerStatus.canChangeCamera"
        icon-material="cameraswitch"
        @click="toggleCamera"
      />
    </div>
  </div>
</template>

<script setup>
// @ts-check
import QRScanner from '@/js/services/QRScanner';
import eventBus from '@/js/event-bus';
import { HiNotification } from '@/js/common/HINotificationSupport';
import { f7 } from 'framework7-vue';
import {
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  activeScan: {
    type: Boolean,
    default: false,
  },
  showScanOverlay: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['scanner:scan']);

const { t } = useI18n();
const finishToLoad = ref(false);

const scannerStatus = reactive({
  lightEnabled: false,
  canEnableLight: false,
  canChangeCamera: false,
});

watch(() => props.activeScan, (value) => {
  if (value) {
    initScanner();
  } else {
    stopScanner();
  }
});

async function initScanner() {
  finishToLoad.value = false;
  try {
    f7.preloader.show();
    QRScanner.prepareScanner(emitScanned);
  } catch {
    HiNotification.Alert(t('errorPermissionCamera'));
  } finally {
    setTimeout(() => {
      f7.preloader.hide();
    }, 1200);
  }
  finishToLoad.value = true;
}

/**
 * @param {QRScannerStatus} newScannerStatus
 * @returns {void}
 */
function setScannerOptions(newScannerStatus) {
  scannerStatus.canEnableLight = newScannerStatus?.canEnableLight;
  scannerStatus.canChangeCamera = newScannerStatus?.canChangeCamera;
  scannerStatus.lightEnabled = newScannerStatus?.lightEnabled;
}

function resetScannerOptions() {
  scannerStatus.canEnableLight = false;
  scannerStatus.canChangeCamera = false;
}

/**
 * @public
 * @returns {void}
 */
function stopScanner() {
  QRScanner.destroyScanner();
}

function toggleCamera() {
  QRScanner.toggleCamera();
}

function changeFlashStatus() {
  scannerStatus.lightEnabled = !scannerStatus.lightEnabled;
  QRScanner.toggleFlash(scannerStatus.lightEnabled);
}

/**
 * @param {string} data
 * @returns {void}
 */
function emitScanned(data) {
  emit('scanner:scan', data);
}

onMounted(() => {
  eventBus.on('openingScanner', setScannerOptions);
  eventBus.on('closingScanner', resetScannerOptions);
  if (props.activeScan) {
    initScanner();
  }
});

onBeforeUnmount(() => {
  stopScanner();
  eventBus.off('openingScanner');
  eventBus.off('closingScanner');
});

defineExpose({
  stopScanner,
});
</script>

<style scoped lang="scss">
.main-qrscanner {
  &-actions {
    display: flex;
    position: absolute;
    bottom: 15vh;
    justify-content: center;
    width: 100%;

    a {
      background-color: #29af97;
    }
  }

  &-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200px;
    height: 200px;
    transform: translate(-50%, -50%);
  }

  .corner {
    position: absolute;
    width: 30px;
    height: 30px;
    border-style: solid;
  }

  .top-left {
    border-width: 7px 0 0 7px;
    border-color: var(--his-tierra-white);
    top: 0;
    left: 0;
  }

  .top-right {
    border-width: 7px 7px 0 0;
    border-color: var(--his-tierra-white);
    top: 0;
    right: 0;
  }

  .bottom-left {
    border-width: 0 0 7px 7px;
    border-color: var(--his-tierra-white);
    bottom: 0;
    left: 0;
  }

  .bottom-right {
    border-width: 0 7px 7px 0;
    border-color: var(--his-tierra-white);
    bottom: 0;
    right: 0;
  }

  .scanner-line {
    position: absolute;
    top: 0;
    left: -5%;
    width: 110%;
    height: 2px;
    background-color: #29af97;
    animation: scanning 2s linear infinite;
  }
}

@keyframes scanning {
  0% { top: 0; }
  50% { top: 100%; }
  100% { top: 0; }
}
</style>
